import type { FC } from 'react'
import { useEffect, useState } from 'react'
import styled from '@emotion/styled'
import type { AnimationItem } from 'lottie-web'
import { useTheme } from '@emotion/react'

import SectionBase, * as Section from '@segment/matcha/recipes/Section'
import CopyTextBase, * as CopyText from '@segment/matcha/recipes/CopyText'

import useLottie from '@segment/utils/useLottie/useLottie'
import useIsInView from '@segment/utils/useInView'

import jsonUrl from './assets/the-omnichannel-imperative-lottie.json.data'

const HEADLINE = `The omnichannel imperative.`
const Description: FC = () => {
  const theme = useTheme()
  return (
    <>
      <CopyText.Description tag="p" variant="pLarge">
        More and more consumers (69% to be specific) expect a personal and consistent customer experience across
        multiple different channels, both physical and digital. Yet few businesses are following through with it.
      </CopyText.Description>
      <CopyText.Description tag="p" variant="pLarge">
        Just 24% of businesses say they are investing successfully in omnichannel personalization.
      </CopyText.Description>
      <CopyText.Description tag="p" variant="h6" tokens={{ color: theme.palette.twilio.gray[100] }}>
        Rugs.com unified its owned and paid channels to achieve 400% revenue growth
      </CopyText.Description>
    </>
  )
}

const link = {
  href: `/customers/rugs-dot-com/`,
  text: `Read how`,
}

const TheOmnichannelImperative: FC = () => {
  const [animationData, setAnimationData] = useState<Promise<Response>>()
  useEffect(() => {
    setAnimationData(fetch(jsonUrl))
  }, [])
  const [animation, containerRef] = useLottie<AnimationItem, HTMLDivElement>({
    options: {
      autoplay: false,
      animationData,
      loop: false,
    },
  })
  const isInView = useIsInView(containerRef)

  useEffect(() => {
    if (isInView) {
      animation?.play()
    }
  }, [animation, isInView])

  return (
    <SectionBase variant="2Col">
      <Section.Contents alignSelf="center">
        <Animation ref={containerRef} />
      </Section.Contents>
      <Section.Copy alignSelf="center">
        <CopyTextBase>
          <CopyText.Heading tag="h2" variant="h2">
            {HEADLINE}
          </CopyText.Heading>
          <Description />
          <CopyText.Link size="medium" href={link.href}>
            {link.text}
          </CopyText.Link>
        </CopyTextBase>
      </Section.Copy>
    </SectionBase>
  )
}

export default TheOmnichannelImperative

const Animation = styled.div`
  max-width: 640px;
  margin: 0 auto;
  aspect-ratio: 560 / 280;
`
